// components/VoiceRemarks.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, message, Tag, Space } from 'antd';
import { AudioOutlined, StopOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const VoiceRemarks = ({ value, onChange, placeholder = "Enter remarks..." }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [interimTranscript, setInterimTranscript] = useState('');
    const [isSupported, setIsSupported] = useState(false);
    const recognitionRef = useRef(null);

    useEffect(() => {
        // Check if browser supports speech recognition
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        
        if (SpeechRecognition) {
            setIsSupported(true);
            recognitionRef.current = new SpeechRecognition();
            
            // Configure recognition
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = true;
            recognitionRef.current.lang = 'en-US';
            
            recognitionRef.current.onstart = () => {
                setIsRecording(true);
                message.success('Listening... Speak now');
            };
            
            recognitionRef.current.onresult = (event) => {
                let interimText = '';
                let finalText = '';
                
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const result = event.results[i];
                    const text = result[0].transcript;
                    
                    if (result.isFinal) {
                        finalText += text + ' ';
                    } else {
                        interimText += text;
                    }
                }
                
                if (finalText) {
                    setTranscript(prev => prev + finalText);
                }
                setInterimTranscript(interimText);
            };
            
            recognitionRef.current.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                setIsRecording(false);
                
                if (event.error === 'no-speech') {
                    message.warning('No speech detected. Please try again.');
                } else if (event.error === 'not-allowed') {
                    message.error('Microphone access denied. Please allow microphone access.');
                } else {
                    message.error(`Recognition error: ${event.error}`);
                }
            };
            
            recognitionRef.current.onend = () => {
                setIsRecording(false);
            };
        }
        
        return () => {
            if (recognitionRef.current && isRecording) {
                recognitionRef.current.stop();
            }
        };
    }, []);

    const startRecording = () => {
        if (!recognitionRef.current) return;
        
        try {
            setTranscript('');
            setInterimTranscript('');
            recognitionRef.current.start();
        } catch (error) {
            console.error('Failed to start recording:', error);
            message.error('Failed to start recording');
        }
    };

    const stopRecording = () => {
        if (recognitionRef.current && isRecording) {
            recognitionRef.current.stop();
            setIsRecording(false);
            message.info('Recording stopped');
        }
    };

    const applyTranscript = () => {
        const fullTranscript = transcript.trim();
        if (fullTranscript) {
            const newValue = value 
                ? `${value}\n\n${fullTranscript}`
                : fullTranscript;
            onChange(newValue);
            setTranscript('');
            setInterimTranscript('');
            message.success('Voice notes added to remarks');
        }
    };

    const clearTranscript = () => {
        setTranscript('');
        setInterimTranscript('');
    };

    if (!isSupported) {
        return (
            <div>
                <TextArea 
                    value={value} 
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder}
                    rows={4}
                />
                <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
                    Voice input is not supported in this browser. Please use Chrome or Edge.
                </div>
            </div>
        );
    }

    return (
        <div>
            <TextArea 
                value={value} 
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                rows={4}
                style={{ marginBottom: 8 }}
            />
            
            {/* Voice Recording Controls */}
            <Space style={{ marginBottom: 12 }}>
                {!isRecording ? (
                    <Button 
                        type="primary"
                        icon={<AudioOutlined />}
                        onClick={startRecording}
                        ghost
                    >
                        Start Voice Input
                    </Button>
                ) : (
                    <Button 
                        danger
                        icon={<StopOutlined />}
                        onClick={stopRecording}
                    >
                        Stop Recording
                    </Button>
                )}
                
                {transcript && (
                    <>
                        <Button 
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={applyTranscript}
                        >
                            Apply to Remarks
                        </Button>
                        <Button 
                            icon={<DeleteOutlined />}
                            onClick={clearTranscript}
                        >
                            Clear
                        </Button>
                    </>
                )}
            </Space>
            
            {/* Recording Status */}
            {isRecording && (
                <div style={{ 
                    marginBottom: 12,
                    padding: 12,
                    background: '#e6f7ff',
                    border: '1px solid #91d5ff',
                    borderRadius: 4
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            background: '#ff4d4f',
                            marginRight: 8,
                            animation: 'pulse 1.5s infinite'
                        }} />
                        <strong>Recording in progress...</strong>
                    </div>
                    {interimTranscript && (
                        <div style={{ 
                            fontSize: 12, 
                            color: '#666',
                            fontStyle: 'italic' 
                        }}>
                            {interimTranscript}
                        </div>
                    )}
                </div>
            )}
            
            {/* Transcript Preview */}
            {transcript && (
                <div style={{ 
                    padding: 12,
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    borderRadius: 4,
                    marginBottom: 12
                }}>
                    <div style={{ marginBottom: 8 }}>
                        <Tag color="success">Voice Transcript</Tag>
                    </div>
                    <div style={{ fontSize: 13 }}>
                        {transcript}
                    </div>
                </div>
            )}
            
            <style jsx>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
            `}</style>
        </div>
    );
};

export default VoiceRemarks;